import { refreshAccessTokenWithRefreshToken } from '@/functions/auth';
import FailedQueueMonostate from '@/utils/monostates/failedQueueMonostate';
import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { createResponseInterceptor } from './createInterceptor';

export type CustomConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export type CustomAxiosError = AxiosError & {
  config: CustomConfig;
};

let refreshTokenPromise: Promise<boolean> | null = null;

const failedQueueMonostate = new FailedQueueMonostate();

const processQueue = (error: unknown) => {
  const failedQueue = failedQueueMonostate.getValue();
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(null);
    }
  });

  failedQueueMonostate.clear();
};

function addToFailedQueue(
  originalRequest: InternalAxiosRequestConfig,
  client: AxiosInstance,
) {
  return new Promise((resolve, reject) => {
    failedQueueMonostate.add([{ resolve, reject }]);
  })
    .then(() => {
      return client(originalRequest);
    })
    .catch(err => {
      return Promise.reject(err);
    });
}

async function getRefreshTokenPromise(
  client: AxiosInstance,
): Promise<boolean | undefined> {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = refreshAccessTokenWithRefreshToken(client)
    .catch(failedToGetNewToken)
    .then(token => {
      return token;
    })
    .finally(() => {
      refreshTokenPromise = null;
    });
  return refreshTokenPromise;
}

async function getNewToken(
  client: AxiosInstance,
  originalRequest: InternalAxiosRequestConfig,
) {
  const newToken = await getRefreshTokenPromise(client);
  if (newToken) {
    processQueue(null);
    return client(originalRequest);
  }
}

function failedToGetNewToken(error: unknown) {
  processQueue(error);
  return Promise.reject(error);
}

const tryGetNewToken = async (
  client: AxiosInstance,
  originalRequest: CustomConfig,
) => {
  originalRequest._retry = true;
  try {
    if (refreshTokenPromise) {
      return addToFailedQueue(originalRequest, client);
    }
    return getNewToken(client, originalRequest);
  } catch (refreshError) {
    return failedToGetNewToken(refreshError);
  }
};

export async function handleRefreshTokenInterceptorError(
  error: CustomAxiosError,
  client: AxiosInstance,
) {
  const originalRequest = error.config;

  if (error.response?.status !== 401 || originalRequest._retry)
    return Promise.reject(error);

  if (originalRequest.url?.includes('auth/token/'))
    return Promise.reject(error);

  return tryGetNewToken(client, originalRequest);
}

export function createRefreshTokenInterceptor(client: AxiosInstance) {
  createResponseInterceptor({
    client,
    onFulfilled: response => response,
    onRejected: error => handleRefreshTokenInterceptorError(error, client),
  });
}
